/**
 * The trainee's practice sandbox, as this browser knows it.
 *
 * The server owns the sandbox: it decides the fleet, holds the lease, and
 * narrows the owner's control scope while it is open. This store opens it,
 * renews the lease while the Tutorial Center is on screen, and closes it on the
 * way out. A browser that vanishes without closing simply stops renewing, and
 * the server sweeps the fleet away when the lease runs out.
 */

import { create } from 'zustand';

import { closePractice, openPractice, renewPractice, type PracticeSession } from '@/api/practice';

export type PracticeStatus = 'idle' | 'opening' | 'open' | 'refused' | 'closing';

interface PracticeState {
  status: PracticeStatus;
  session: PracticeSession | null;
  /** Why the sandbox would not open, in the server's words. */
  error: string | null;

  open: (lat: number, lon: number) => Promise<void>;
  renew: (lat: number, lon: number) => Promise<void>;
  close: () => Promise<void>;
}

export const usePracticeStore = create<PracticeState>((set, get) => ({
  status: 'idle',
  session: null,
  error: null,

  open: async (lat, lon) => {
    if (get().status === 'opening') return;
    set({ status: 'opening', error: null });
    const result = await openPractice(lat, lon);
    if (!result.ok) {
      set({ status: 'refused', session: null, error: result.error.message });
      return;
    }
    set({ status: 'open', session: result.data.session, error: null });
  },

  renew: async (lat, lon) => {
    if (get().status !== 'open') return;
    const result = await renewPractice();
    if (result.ok) {
      set({ session: result.data.session });
      return;
    }
    // The lease ran out while this tab was asleep and the server swept the
    // fleet. Open a fresh one rather than renewing nothing forever.
    if (result.status === 404) await get().open(lat, lon);
  },

  close: async () => {
    if (get().status === 'idle') return;
    set({ status: 'closing' });
    await closePractice();
    set({ status: 'idle', session: null, error: null });
  },
}));

const NO_IDS: readonly string[] = [];

export function usePracticeIds(): readonly string[] {
  return usePracticeStore((state) => state.session?.droneIds ?? NO_IDS);
}
